// Visible, crawlable help content for Number Line Drop.
// Shaped by what learners search: "where does pi go on a number line",
// "irrational vs rational examples", "22/7 vs pi", "number sense estimation".
export default function NumberLineDropSeo() {
  return (
    <>
      <h2 style={{ color: "#E8E9F3", fontSize: 19, margin: "0 0 12px" }}>
        Number sense before number facts
      </h2>
      <p style={{ margin: "0 0 16px" }}>
        Students who develop fast math intuition tend to share one habit: they
        can visualize where numbers live in relation to each other. They feel
        that 7/8 is just under 1, that √2 is between 1 and 2 (close to 1.4),
        that π is about 3.14 and 22/7 is suspiciously similar but not the
        same. This visualization is called the number-line representation,
        and it does most of the heavy lifting in math fluency long after the
        formulas are memorized. Number Line Drop trains it directly — you
        pick up a value, you place it on a line, you see how close you were.
      </p>

      <h2 style={{ color: "#E8E9F3", fontSize: 19, margin: "24px 0 12px" }}>
        The five rounds and what each teaches
      </h2>
      <p style={{ margin: "0 0 16px" }}>
        Round 1 is the warmup — integers and halves on a 0-to-5 line, just
        to feel the placement mechanic. Round 2 is the famous irrationals:
        π, e, √2, √3 on the same scale, the four numbers every math student
        should be able to find blindfolded. Round 3 takes those same constants
        and operates on them — π/2, e², 2π, √2+1 — which forces you to feel
        rather than recall. Round 4 is the hardest and the point of the
        game: a tight number line from 3.0 to 3.3 where you place π, 22/7,
        157/50, and 355/113 — four very close values that test real
        precision. This round teaches that "close" is a real mathematical
        idea: Archimedes used 22/7 as an approximation to π, off by 0.04%.
        Chinese mathematician Zu Chongzhi found 355/113 in the 5th century,
        accurate to six decimal places. Round 5 widens the line to include
        negatives and the golden ratio.
      </p>

      <h2 style={{ color: "#E8E9F3", fontSize: 19, margin: "24px 0 12px" }}>
        What "close" means in scoring
      </h2>
      <p style={{ margin: "0 0 16px" }}>
        Every drop is graded on linear distance from the true position,
        normalized to the range of the line. Within the round's tolerance
        you get full credit. Outside it, accuracy decays linearly over the
        next three tolerance widths until it hits zero. The tolerances are
        wider on rounds with familiar values and tighter on the precision
        round — by Round 4 you need to be within roughly 0.8% of the line's
        range to score, which means π and 22/7 are distinguishable even
        though they look almost identical on screen.
      </p>

      <h2 style={{ color: "#E8E9F3", fontSize: 19, margin: "24px 0 12px" }}>
        Honest about what this game is
      </h2>
      <p style={{ margin: "0 0 16px" }}>
        Number Line Drop is not a substitute for understanding what π or e
        actually are. It does not derive them, prove their irrationality, or
        explain their applications. What it does is build the intuition that
        these numbers occupy specific positions on the real line, and that
        you should know where. The detail notes after each drop teach
        history (the Archimedes / Zu Chongzhi stories, the silver ratio,
        why φ shows up everywhere) — that part is the game treating you as
        a curious person rather than a student.
      </p>
    </>
  );
}
